import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { ThemeProvider } from './context/ThemeContext'
import Layout from './components/Layout'
import RequireAuth from './components/RequireAuth'
import RequireProtect from './components/RequireProtect'
import ProtectedRoute from './components/ProtectedRoute'
import PageTransition from './components/PageTransition'
import PageSkeleton from './components/PageSkeleton'
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import LoginOtpPage from './pages/LoginOtpPage'
import SignupPage from './pages/signup/SignupPage'
import ProtectPage from './pages/ProtectPage'
import DeckPage from './pages/DeckPage'
import Deck2Page from './pages/Deck2Page'
import Deck3Page from './pages/Deck3Page'
import DeckFinalPage from './pages/DeckFinalPage'
import Deck4Page from './pages/Deck4Page'
import ProductDeckPage from './pages/ProductDeckPage'
import MasterPlanPage from './pages/MasterPlanPage'
import PlanRichardPage from './pages/PlanRichardPage'
import PrivacyPolicyPage from './pages/legal/PrivacyPolicyPage'
import TermsOfServicePage from './pages/legal/TermsOfServicePage'
import AccessibilityStatementPage from './pages/legal/AccessibilityStatementPage'

// ── Lazy-loaded pages ──────────────────────────────────────────────
// Each page is loaded on-demand so sidebar navigation is instant.
// The <Suspense> boundary shows a lightweight skeleton while the
// chunk downloads / parses.
const ShowsPage = lazy(() => import('./pages/ShowsPage'))
const SchedulePage = lazy(() => import('./pages/SchedulePage'))
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'))
const TeamMemberEditPage = lazy(() => import('./pages/TeamMemberEditPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const GoogleCalendarCallbackPage = lazy(() => import('./pages/GoogleCalendarCallbackPage'))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'))
const MusicPage = lazy(() => import('./pages/MusicPage'))
const MusicDashTestPage = lazy(() => import('./pages/MusicDashTestPage'))
const TeamHubPage = lazy(() => import('./pages/TeamHubPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const ChatPage = lazy(() => import('./pages/ChatPage'))
const TasksPage = lazy(() => import('./pages/TasksPage'))
const OverviewPage = lazy(() => import('./pages/OverviewPage'))

/**
 * Redirect team members (manager/support) without a selected artist to Team Hub.
 * Regular artists go to /shows.
 */
function SmartRedirect() {
  try {
    const raw = localStorage.getItem('dizeeUser')
    const user = raw ? JSON.parse(raw) : null
    const isTeamMember = user?.professionRole && ['manager', 'support', 'promoter'].includes(user.professionRole)
    const hasSelectedArtist = !!localStorage.getItem('selectedArtist')

    if (isTeamMember && !hasSelectedArtist) {
      return <Navigate to="/team-hub" replace />
    }
  } catch {
    // fall through to default
  }
  return <Navigate to="/shows" replace />
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Homepage */}
          <Route
            path="/home"
            element={<HomePage />}
          />

          {/* Investor Deck */}
          <Route path="/deck" element={<DeckPage />} />
          {/* Investor Deck v2 — simple, emotional + mocks */}
          <Route path="/deck2" element={<Deck2Page />} />
          {/* Investor Deck v3 — refined (conversation changes, gray boxes, text left + imagery right) */}
          <Route path="/deck3" element={<Deck3Page />} />
          {/* Investor Deck Final — copy of deck3 for post-call edits */}
          <Route path="/deck-final" element={<DeckFinalPage />} />
          {/* Investor Deck v4 — simplified version of deck3 */}
          <Route path="/deck4" element={<Deck4Page />} />
          {/* Product Deck */}
          <Route path="/product-deck" element={<ProductDeckPage />} />
          {/* Master Plan (password-protected) */}
          <Route path="/plan" element={<MasterPlanPage />} />
          {/* Plan Richard — Phase 1 & 2 deliverables (same password) */}
          <Route path="/plan-richard" element={<PlanRichardPage />} />

          {/* Public Legal Pages */}
          <Route
            path="/privacy-policy"
            element={
              <PageTransition>
                <PrivacyPolicyPage />
              </PageTransition>
            }
          />
          <Route
            path="/terms-of-service"
            element={
              <PageTransition>
                <TermsOfServicePage />
              </PageTransition>
            }
          />
          <Route
            path="/accessibility-statement"
            element={
              <PageTransition>
                <AccessibilityStatementPage />
              </PageTransition>
            }
          />

          {/* Auth Pages */}
          <Route
            path="/protect"
            element={
              <PageTransition>
                <ProtectPage />
              </PageTransition>
            }
          />
          <Route
            path="/login"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/login-otp"
            element={
              <RequireProtect>
                <PageTransition>
                  <LoginOtpPage />
                </PageTransition>
              </RequireProtect>
            }
          />
          <Route
            path="/signup"
            element={
              <RequireProtect>
                <PageTransition>
                  <SignupPage />
                </PageTransition>
              </RequireProtect>
            }
          />

          {/* Authenticated App — Sidebar Layout */}
          <Route
            path="/"
            element={
              <RequireProtect>
                <RequireAuth>
                  <Layout />
                </RequireAuth>
              </RequireProtect>
            }
          >
            <Route index element={<SmartRedirect />} />
            <Route path="overview" element={<ProtectedRoute requiredPermission="overview"><Suspense fallback={<PageSkeleton />}><OverviewPage /></Suspense></ProtectedRoute>} />
            <Route path="team-hub" element={<Suspense fallback={<PageSkeleton />}><TeamHubPage /></Suspense>} />
            <Route path="dashboard" element={<Suspense fallback={<PageSkeleton />}><DashboardPage /></Suspense>} />
            <Route path="promoter" element={<Navigate to="/dashboard" replace />} />
            <Route path="manager-dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="support-dashboard" element={<Navigate to="/dashboard" replace />} />
            <Route path="chat" element={<Suspense fallback={<PageSkeleton />}><ChatPage /></Suspense>} />
            <Route path="schedule" element={<ProtectedRoute requiredPermission="schedule"><Suspense fallback={<PageSkeleton />}><SchedulePage /></Suspense></ProtectedRoute>} />
            <Route path="shows" element={<ProtectedRoute requiredPermission="shows"><Suspense fallback={<PageSkeleton />}><ShowsPage /></Suspense></ProtectedRoute>} />
            <Route path="tasks" element={<ProtectedRoute requiredPermission="tasks"><Suspense fallback={<PageSkeleton />}><TasksPage /></Suspense></ProtectedRoute>} />
            <Route path="knowledge" element={<ProtectedRoute requiredPermission="knowledge"><Suspense fallback={<PageSkeleton />}><KnowledgePage /></Suspense></ProtectedRoute>} />
            <Route path="music" element={<ProtectedRoute requiredPermission="music"><Suspense fallback={<PageSkeleton />}><MusicPage /></Suspense></ProtectedRoute>} />
            <Route path="music-test" element={<ProtectedRoute requiredPermission="music"><Suspense fallback={<PageSkeleton />}><MusicDashTestPage /></Suspense></ProtectedRoute>} />
            <Route path="team" element={<ProtectedRoute requiredPermission="team"><Suspense fallback={<PageSkeleton />}><TeamManagementPage /></Suspense></ProtectedRoute>} />
            <Route path="team/:teamMemberId/edit" element={<ProtectedRoute requiredPermission="team"><Suspense fallback={<PageSkeleton />}><TeamMemberEditPage /></Suspense></ProtectedRoute>} />
            <Route path="settings" element={<ProtectedRoute requiredPermission="settings"><Suspense fallback={<PageSkeleton />}><SettingsPage /></Suspense></ProtectedRoute>} />
            <Route path="google-calendar-callback" element={<ProtectedRoute requiredPermission="schedule"><Suspense fallback={<PageSkeleton />}><GoogleCalendarCallbackPage /></Suspense></ProtectedRoute>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  )
}

export default App
